/**
 * DryContext — React context that provides DryStuff services to the entire app.
 *
 * Wires together: DryStore (database), DryConnect (HTTP), DryAuth (tokens),
 * SyncManager (offline pre-download), and NetworkMonitor.
 *
 * Usage:
 *   <DryProvider>
 *     <App />
 *   </DryProvider>
 *
 * In components:
 *   const { db, http, auth, sync, network } = useDry();
 *   const { data } = useQuery(db.assets, { selector: { status: 'active' } });
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { Collection, Doc } from '@drystuff/store';
import type { HttpClient } from '@drystuff/connect';
import type { NotificationManager } from '@drystuff/notify';
import type { SyncManager } from './sync-manager';
import type { User, Tenant, LoginRequest, RegisterRequest, AuthResponse } from '../types';
import { setPendingRefreshToken, createSecureVault, setCachedRefreshToken, resetReconnect } from './secure';
import { resetUnlockSession } from '../hooks/useOfflineUnlock';

// Types for the context value
export interface DryCollections {
  users: Collection<any>;
  assets: Collection<any>;
  assetTypes: Collection<any>;
  sites: Collection<any>;
  formTemplates: Collection<any>;
  formSubmissions: Collection<any>;
  notifications: Collection<any>;
}

export interface DryContextValue {
  /** All DryStore collections */
  db: DryCollections;
  /** DryConnect HTTP client */
  http: HttpClient;
  /** DryAuth manager */
  auth: any;
  /** Network monitor (online/offline) */
  network: any;
  /** Sync manager for pre-downloading data */
  sync: SyncManager;
  /** DryNotify notification manager */
  notificationManager: NotificationManager | null;
  /** DryConnect WebSocket client (available after login) */
  wsClient: any;
  /** Whether the initial database setup is complete */
  ready: boolean;
  /** Whether the initial sync has completed */
  synced: boolean;
  /** Whether the app is online */
  isOnline: boolean;
  /** Offline submission-capture replication pending/failed counts */
  pendingSync: { pending: number; failed: number };
  /** Current authenticated user */
  user: User | null;
  /** Current tenant */
  tenant: Tenant | null;
  /** Tenant terminology overrides; empty = use platform defaults */
  labels: Record<string, { singular: string; plural: string }>;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth is still loading */
  isAuthLoading: boolean;
  /** Login action */
  login: (credentials: LoginRequest) => Promise<void>;
  /** Register action */
  register: (data: RegisterRequest) => Promise<void>;
  /** Logout action */
  logout: () => Promise<void>;
  /** Refresh user data from server */
  refreshUser: () => Promise<void>;
}

const DryCtx = createContext<DryContextValue | null>(null);

/**
 * Hook to access DryStuff services.
 */
export function useDry(): DryContextValue {
  const ctx = useContext(DryCtx);
  if (!ctx) throw new Error('useDry() must be used within <DryProvider>');
  return ctx;
}

/**
 * Hook to access a specific collection.
 */
export function useCollection<T extends Doc = any>(name: keyof DryCollections): Collection<T> {
  const { db } = useDry();
  return db[name] as Collection<T>;
}

interface DryProviderProps {
  children: React.ReactNode;
}

/**
 * DryProvider — initializes the DryStuff stack and provides it to the app.
 *
 * On mount:
 * 1. Creates IndexedDB-backed DryStore database with all collections
 * 2. Creates DryConnect HTTP client + DryAuth manager
 * 3. Creates SyncManager for offline pre-download
 * 4. Creates DryNotify notification manager
 *
 * On login (when auth token is available):
 * 5. Stores user/tenant in state and localStorage
 * 6. Creates DryConnect WebSocket for real-time updates
 * 7. Runs initial sync to pre-download forms, assets, sites
 * 8. Starts periodic background sync
 *
 * On logout:
 * 9. Stops periodic sync, closes WebSocket
 * 10. Clears localStorage and DryStore collections
 */
export function DryProvider({ children }: DryProviderProps) {
  const [ready, setReady] = useState(false);
  const [synced, setSynced] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  // Tenant terminology overrides. Loaded from login response and
  // re-hydrated on page refresh from localStorage so first paint is
  // always branded correctly. Empty object = use defaults.
  const [labels, setLabels] = useState<Record<string, { singular: string; plural: string }>>({});
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [wsClient, setWsClient] = useState<any>(null);
  // Pending-sync counts for the capture replication pipeline, surfaced to the
  // UI (e.g. the header pending-sync indicator) via the context value.
  const [pendingSync, setPendingSync] = useState<{ pending: number; failed: number }>({ pending: 0, failed: 0 });
  // valueRef holds the stable service handles (built once on init) that the
  // login/logout/refresh handlers reach for — a subset of the full context value.
  const valueRef = useRef<Pick<
    DryContextValue,
    'db' | 'http' | 'auth' | 'network' | 'sync' | 'notificationManager'
  > | null>(null);
  const wsClientRef = useRef<any>(null);
  // Holds the unsubscribe fn for the capture replication status$ subscription
  // so we can tear it down on logout / re-login / unmount (avoids a leak).
  const captureStatusUnsubRef = useRef<(() => void) | null>(null);

  // ─── Auth lifecycle callbacks (stable via useCallback) ───

  // Start the offline submission-capture replication and subscribe to its
  // SyncStateTracker status, mirroring pending/failed counts into React state.
  // Called from the three places an authenticated session begins: login,
  // register, and the page-refresh restore path. Safe to call repeatedly —
  // it stops any stale replication and unsubscribes the prior status$ first
  // (re-login after a destroyed DB must not keep a tracker bound to the old
  // collection). reconcilePendingSubmissions (inside startCaptureReplication)
  // re-enqueues submissions captured before this restart, which is why the
  // restore path must call it too — a PWA refresh never hits the login handler.
  const startCaptureSync = useCallback(async (
    vRef: Pick<DryContextValue, 'db' | 'http'>,
  ) => {
    try {
      const { startCaptureReplication, stopCaptureReplication } = await import('./capture-replication');
      // Ensure no stale replication/subscription from a prior session.
      captureStatusUnsubRef.current?.();
      captureStatusUnsubRef.current = null;
      stopCaptureReplication();
      const status$ = startCaptureReplication(vRef.db as any, vRef.http);
      captureStatusUnsubRef.current = status$.subscribe((s) =>
        setPendingSync({ pending: s.pending, failed: s.failed }),
      );
    } catch (err) {
      console.warn('[DryProvider] Capture replication start failed (non-fatal):', (err as Error).message);
    }
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    setIsAuthLoading(true);
    try {
      const vRef = valueRef.current;
      if (!vRef) throw new Error('DryStuff not initialized');

      const response = await vRef.http.request<AuthResponse>({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      });
      const authData = response.data;

      // Capture the refresh token in memory for offline-unlock enrollment.
      setPendingRefreshToken(authData.refresh_token ?? null);

      // Store token (backward compat with existing localStorage keys)
      localStorage.setItem('auth_token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      if (authData.tenant) {
        localStorage.setItem('tenant', JSON.stringify(authData.tenant));
      } else {
        localStorage.removeItem('tenant');
      }

      // Store user in DryStore users collection
      try {
        await vRef.db.users.upsert(authData.user);
      } catch (err) {
        console.warn('[DryProvider] user upsert failed (non-fatal)', err);
      }

      setUser(authData.user);
      setTenant(authData.tenant || null);
      // Persist labels for refresh-from-localStorage path. Empty / absent
      // = no override; the resolver falls back to platform defaults.
      const incomingLabels = authData.labels || {};
      setLabels(incomingLabels);
      try {
        localStorage.setItem('tenant_labels', JSON.stringify(incomingLabels));
      } catch { /* quota; non-fatal */ }
      setIsAuthLoading(false);

      // Create WebSocket connection
      try {
        const { createAppWebSocket } = await import('./client');
        const ws = createAppWebSocket(authData.access_token);
        wsClientRef.current = ws;
        setWsClient(ws);
      } catch (err) {
        console.warn('[DryProvider] WebSocket creation failed (non-fatal):', (err as Error).message);
      }

      // Trigger initial sync
      try {
        await vRef.sync.initialSync();
        vRef.sync.startPeriodicSync();
      } catch (err) {
        console.warn('[DryProvider] Initial sync failed (may be offline):', (err as Error).message);
      }

      // Start offline submission-capture replication for this session.
      await startCaptureSync(vRef);

      console.log('[DryProvider] Login successful');
    } catch (error) {
      setIsAuthLoading(false);
      throw error;
    }
  }, [startCaptureSync]);

  const register = useCallback(async (data: RegisterRequest) => {
    setIsAuthLoading(true);
    try {
      const vRef = valueRef.current;
      if (!vRef) throw new Error('DryStuff not initialized');

      const response = await vRef.http.request<AuthResponse>({
        url: '/auth/register',
        method: 'POST',
        body: data,
      });
      const authData = response.data;

      // Capture the refresh token in memory for offline-unlock enrollment.
      setPendingRefreshToken(authData.refresh_token ?? null);

      localStorage.setItem('auth_token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      if (authData.tenant) {
        localStorage.setItem('tenant', JSON.stringify(authData.tenant));
      } else {
        localStorage.removeItem('tenant');
      }

      try {
        await vRef.db.users.upsert(authData.user);
      } catch (err) {
        console.warn('[DryProvider] user upsert failed (non-fatal)', err);
      }

      setUser(authData.user);
      setTenant(authData.tenant || null);
      setIsAuthLoading(false);

      // Create WebSocket connection
      try {
        const { createAppWebSocket } = await import('./client');
        const ws = createAppWebSocket(authData.access_token);
        wsClientRef.current = ws;
        setWsClient(ws);
      } catch (err) {
        console.warn('[DryProvider] WebSocket creation failed (non-fatal):', (err as Error).message);
      }

      // Trigger initial sync
      try {
        await vRef.sync.initialSync();
        vRef.sync.startPeriodicSync();
      } catch (err) {
        console.warn('[DryProvider] Initial sync failed:', (err as Error).message);
      }

      // Start offline submission-capture replication for this session.
      await startCaptureSync(vRef);

      console.log('[DryProvider] Registration successful');
    } catch (error) {
      setIsAuthLoading(false);
      throw error;
    }
  }, [startCaptureSync]);

  const logout = useCallback(async () => {
    const vRef = valueRef.current;
    try {
      if (vRef) {
        const token = localStorage.getItem('auth_token');
        await vRef.http.request<any>({ url: '/auth/logout', method: 'POST', body: { access_token: token } }).catch(() => {});
      }
    } catch { /* ignore */ }

    // Close WebSocket
    if (wsClientRef.current) {
      try { wsClientRef.current.dispose?.() ?? wsClientRef.current.disconnect?.(); } catch { /* ignore */ }
      wsClientRef.current = null;
      setWsClient(null);
    }

    // Stop sync
    if (vRef) {
      vRef.sync.stopPeriodicSync?.();
    }

    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');

    // Drop the in-memory refresh token captured for offline-unlock enrollment.
    setPendingRefreshToken(null);

    // Clear the offline-unlock module-level singletons. A soft (client-side, no
    // reload) logout — Header does `await logout(); navigate('/login')` — leaves
    // these in memory, so without this the NEXT user in the same tab would inherit
    // the PREVIOUS user's cached refresh token (the 401 interceptor calls
    // getUnlockedRefreshToken() → returns the stale token → mints an access token
    // for the wrong user). Reset both: the cached refresh token and the session
    // `unlocked` flag.
    setCachedRefreshToken(null);
    resetUnlockSession();
    // Drop any shared in-flight refresh promise so a prior user's refresh cannot
    // be reused by the next caller after a soft logout.
    resetReconnect();

    // Wipe the offline-unlock vault: an explicit logout must not be reversible by
    // PIN/biometric unlock of a stale refresh token. (Offline-unlock targets token
    // *expiry*, not logout.)
    try { createSecureVault().wipe(); } catch { /* ignore */ }

    // Stop offline submission-capture replication and tear down its status$
    // subscription before the DB is cleared (the tracker is bound to a
    // collection that clearDatabase() destroys).
    try {
      captureStatusUnsubRef.current?.();
      captureStatusUnsubRef.current = null;
      const { stopCaptureReplication } = await import('./capture-replication');
      stopCaptureReplication();
      setPendingSync({ pending: 0, failed: 0 });
    } catch { /* best-effort */ }

    // Clear all DryStore data + sync checkpoints via the proper API
    try {
      const { clearDatabase } = await import('./database');
      await clearDatabase();
    } catch { /* best-effort */ }

    // Wipe locally-stored inspection photos (separate 'cat-attachments' IndexedDB
    // that clearDatabase doesn't reach) so they don't leak to the next user on a
    // shared device.
    try {
      const { clearAttachments } = await import('./attachments');
      await clearAttachments();
    } catch { /* best-effort */ }

    // Unregister stale service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) { reg.unregister(); }
      } catch { /* best-effort */ }
    }

    setUser(null);
    setTenant(null);
    setLabels({});
    try { localStorage.removeItem('tenant_labels'); } catch { /* ignore */ }
    console.log('[DryProvider] Logged out — cache cleared');
  }, []);

  const refreshUser = useCallback(async () => {
    const vRef = valueRef.current;
    if (!vRef) return;
    try {
      const response = await vRef.http.request<User>({ url: '/auth/me', method: 'GET' });
      const currentUser = response.data;
      setUser(currentUser);
      localStorage.setItem('user', JSON.stringify(currentUser));
    } catch (error) {
      console.error('[DryProvider] Failed to refresh user:', error);
      await logout();
    }
  }, [logout]);

  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        // Lazy import to avoid circular deps and allow code splitting
        const { initDatabase } = await import('./database');
        const { http, auth, network, createAppNotificationManager } = await import('./client');
        const { SyncManager } = await import('./sync-manager');

        const dbInstance = await initDatabase();

        const syncManager = new SyncManager({
          http,
          collections: {
            users: dbInstance.users,
            assets: dbInstance.assets,
            assetTypes: dbInstance.assetTypes,
            sites: dbInstance.sites,
            formTemplates: dbInstance.formTemplates,
            formSubmissions: dbInstance.formSubmissions,
          },
          onSyncComplete: (stats) => {
            if (mounted) setSynced(true);
            console.log('[DryProvider] Sync complete:', stats);
          },
          onSyncError: (err) => {
            console.warn('[DryProvider] Sync error:', err.message);
          },
        });

        // Create DryNotify notification manager
        let notifManager: NotificationManager | null = null;
        try {
          notifManager = createAppNotificationManager(dbInstance.notifications);
          console.log('[DryProvider] DryNotify notification manager created');
        } catch (err) {
          console.warn('[DryProvider] Notification manager setup failed (non-fatal):', (err as Error).message);
        }

        // Wire cache-through interceptors for automatic offline support
        try {
          const { wireCacheInterceptors } = await import('./client');
          wireCacheInterceptors({
            assets: dbInstance.assets,
            sites: dbInstance.sites,
            formTemplates: dbInstance.formTemplates,
            assetTypes: dbInstance.assetTypes,
            formSubmissions: dbInstance.formSubmissions,
          });
          console.log('[DryProvider] Cache-through interceptors wired — offline reads enabled');
        } catch (err) {
          console.warn('[DryProvider] Cache-through setup failed (non-fatal):', (err as Error).message);
        }

        // ─── PWA + DrySW + Push initialization ───
        try {
          const { setupPWA } = await import('./pwa');
          await setupPWA();
          console.log('[DryProvider] PWA registered (DryPWA)');
        } catch (err) {
          console.warn('[DryProvider] PWA setup failed (non-fatal):', (err as Error).message);
        }

        try {
          const { initOfflineSupport, syncSWAuthToken } = await import('./offline');
          const swClient = initOfflineSupport();
          // Sync auth tokens to SW for background operations (no-op if the SW
          // is unavailable, e.g. unsupported browser or insecure context).
          if (swClient) {
            const swAuthSync = swClient.createAuthSync();
            if (typeof auth.subscribe === 'function') {
              auth.subscribe(swAuthSync);
            }
          }
          // The subscribe above fires with the auth manager's in-memory tokens,
          // which aren't hydrated on a fresh page load — push the real token from
          // localStorage so offline replays are authenticated, not dropped.
          syncSWAuthToken();
          console.log('[DryProvider] Offline support initialized (DrySW)');
        } catch (err) {
          console.warn('[DryProvider] Offline support setup failed (non-fatal):', (err as Error).message);
        }

        try {
          const { initPush } = await import('./push');
          initPush();
          console.log('[DryProvider] Push notifications initialized (DryNotify)');
        } catch (err) {
          console.warn('[DryProvider] Push setup failed (non-fatal):', (err as Error).message);
        }

        // Listen for online/offline changes
        const unsubNetwork = network.subscribe?.((status) => {
          if (mounted) setIsOnline(status === 'online');
        });

        // Also listen to browser events directly
        const handleOnline = () => mounted && setIsOnline(true);
        const handleOffline = () => mounted && setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        const value = {
          db: {
            users: dbInstance.users,
            assets: dbInstance.assets,
            assetTypes: dbInstance.assetTypes,
            sites: dbInstance.sites,
            formTemplates: dbInstance.formTemplates,
            formSubmissions: dbInstance.formSubmissions,
            notifications: dbInstance.notifications,
          },
          http,
          auth,
          network,
          sync: syncManager,
          notificationManager: notifManager,
        };

        valueRef.current = value;
        if (mounted) setReady(true);

        // If user is already authenticated (page refresh), restore from localStorage
        const existingToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('user');
        if (existingToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            const storedTenant = localStorage.getItem('tenant');
            const parsedTenant = storedTenant ? JSON.parse(storedTenant) : null;
            if (mounted) {
              setUser(parsedUser);
              setTenant(parsedTenant);
              // Restore tenant terminology overrides. Tolerates absence
              // (older sessions pre-feature) by falling back to empty.
              try {
                const storedLabels = localStorage.getItem('tenant_labels');
                if (storedLabels) setLabels(JSON.parse(storedLabels));
              } catch { /* malformed; keep defaults */ }
            }

            // Create WebSocket for existing session
            try {
              const { createAppWebSocket } = await import('./client');
              const ws = createAppWebSocket(existingToken);
              wsClientRef.current = ws;
              if (mounted) setWsClient(ws);
            } catch (err) {
              console.warn('[DryProvider] WebSocket creation failed (non-fatal):', (err as Error).message);
            }

            // Run initial sync
            try {
              await syncManager.initialSync();
              syncManager.startPeriodicSync();
            } catch (err) {
              console.warn('[DryProvider] Initial sync failed (may be offline):', (err as Error).message);
            }

            // Start offline submission-capture replication for the restored
            // session. A PWA refresh never hits the login handler, so this is
            // where reconcilePendingSubmissions re-enqueues anything captured
            // (queued/failed) before the reload.
            if (valueRef.current) {
              await startCaptureSync(valueRef.current);
            }
          } catch (error) {
            console.error('[DryProvider] Failed to parse stored auth:', error);
            localStorage.removeItem('user');
            localStorage.removeItem('tenant');
            localStorage.removeItem('auth_token');
          }
        }

        if (mounted) setIsAuthLoading(false);

        // Cleanup
        return () => {
          mounted = false;
          syncManager.destroy();
          notifManager?.dispose();
          // Tear down capture replication + its status$ subscription.
          captureStatusUnsubRef.current?.();
          captureStatusUnsubRef.current = null;
          import('./capture-replication').then(({ stopCaptureReplication }) => stopCaptureReplication()).catch(() => {});
          if (wsClientRef.current) {
            try { wsClientRef.current.dispose?.() ?? wsClientRef.current.disconnect?.(); } catch { /* ignore */ }
          }
          if (typeof unsubNetwork === 'function') unsubNetwork();
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
          // Cleanup DrySW + Push
          import('./offline').then(({ disposeOfflineSupport }) => disposeOfflineSupport()).catch(() => {});
          import('./push').then(({ disposePush }) => disposePush()).catch(() => {});
        };
      } catch (err) {
        console.error('[DryProvider] Failed to initialize DryStuff:', err);
        if (mounted) setIsAuthLoading(false);
      }
    }

    init();
  }, []);

  if (!ready || !valueRef.current) {
    // Show loading while DryStuff initializes (typically <100ms)
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#305cde' }}>
            Checks & Ticks
          </div>
          <div style={{ marginTop: '0.5rem', color: '#666' }}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  const contextValue: DryContextValue = {
    ...valueRef.current,
    ready,
    synced,
    isOnline,
    pendingSync,
    user,
    tenant,
    labels,
    isAuthenticated: !!user,
    isAuthLoading,
    login,
    register,
    logout,
    refreshUser,
    wsClient,
  };

  return (
    <DryCtx.Provider value={contextValue}>
      {children}
    </DryCtx.Provider>
  );
}
